// The client's view of the server (§16).
//
// Everything here answers `null` rather than throwing when the server cannot be reached, and that
// is §18 F1 rather than laziness: "Desktop keeps working against OPFS". A shell that treated an
// unreachable server as fatal would turn a network blip into a blank page, and a *guest* build —
// which is every development and e2e run, where nothing is listening on `/api` at all — would
// never boot.
//
// The one place that distinction has teeth is §8.3's toggle: `anonymousAccess` defaults to **true**
// when there is no answer. A server that is down must not lock everybody out of a desktop that
// works offline; only a server that is up and says `false` does that (D-161).
import { publicConfigSchema, meResponseSchema, type MeResponse, type PublicConfig } from '@hangar/shared';

/**
 * §18 F2 — the query parameter `/auth/login` sends the browser back with when sign-in could not
 * happen. Named here, and in `server/src/routes/auth.ts`, from the same reading of §16.
 */
export const SIGNIN_ERROR_PARAM = 'signin_error';

/** How long the boot waits for `/api/config` before deciding there is no server. */
export const SERVER_PROBE_TIMEOUT_MS = 4_000;

export interface ServerAccess {
  /** §16 `GET /api/config` — public. `null` when nothing answered. */
  config(): Promise<PublicConfig | null>;
  /** §16 `GET /api/me` — `null` for a guest *and* for an unreachable server; both mean "no user". */
  me(): Promise<MeResponse | null>;
}

async function getJson(path: string, timeoutMs: number): Promise<unknown | null> {
  if (typeof fetch !== 'function') return null;
  const controller = new AbortController();
  const timer = setTimeout(() => void controller.abort(), timeoutMs);
  try {
    const response = await fetch(path, {
      // The session cookie is host-only and `SameSite=Lax` (§8.2); a same-origin request carries
      // it, and saying so explicitly means a future move to a different origin fails loudly.
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    // Unreachable, aborted, or answering something that is not JSON. All the same to the caller.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export function createServerAccess(timeoutMs = SERVER_PROBE_TIMEOUT_MS): ServerAccess {
  return {
    async config() {
      const body = await getJson('/api/config', timeoutMs);
      // Parsed, not cast: this is the one payload a browser reads before anything is known, and
      // §22 puts every boundary input through a zod schema.
      const parsed = publicConfigSchema.safeParse(body);
      return parsed.success ? parsed.data : null;
    },
    async me() {
      const body = await getJson('/api/me', timeoutMs);
      const parsed = meResponseSchema.safeParse(body);
      return parsed.success ? parsed.data : null;
    },
  };
}

export interface AccessDecision {
  /** §8.3 — render the skin's `LoginScreen` and mount nothing else. */
  readonly locked: boolean;
  readonly signedIn: boolean;
  readonly config: PublicConfig | null;
  readonly me: MeResponse | null;
}

/**
 * §8.3 — may this visitor have a desktop?
 *
 * Only one combination locks: a server that answered, said anonymous access is off, and does not
 * recognise this visitor. Everything else — no server, a server that allows guests, or a signed-in
 * user — gets the desktop.
 */
export async function decideAccess(server: ServerAccess): Promise<AccessDecision> {
  const config = await server.config();
  const me = config === null ? null : await server.me();
  return {
    config,
    me,
    signedIn: me !== null,
    locked: config !== null && !config.anonymousAccess && me === null,
  };
}

/**
 * §16 / §8.1 — start the OIDC dance.
 *
 * A navigation rather than a `fetch`: `/auth/login` answers 302 to Authentik, and following that
 * inside a request would land the issuer's sign-in page somewhere nobody can see it. `onNoServer`
 * covers the guest build, where nothing is listening and PLAN's F2 string is the honest answer.
 *
 * It lives beside the rest of the server talk rather than in the shell root, because the skins'
 * `LoginScreen` needs it too and a skin reaching into `@/App` would be the wrong direction
 * entirely (§6.5).
 */
export function beginSignIn(onNoServer: () => void): void {
  if (typeof window === 'undefined' || typeof window.location?.assign !== 'function') {
    onNoServer();
    return;
  }
  window.location.assign(`/auth/login?redirect=${encodeURIComponent(window.location.pathname)}`);
}
